import { Bar, Pie, Doughnut } from 'react-chartjs-2'
import { useEffect, useRef, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js'

import ChartDataLabels from 'chartjs-plugin-datalabels'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ArcElement
)

export const BarChart = ({ chartData, options }) => {
  // Measure the available width of the scroll area so we can decide
  // whether to fill the container or enable horizontal scrolling.
  const scrollAreaRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState(800)

  useEffect(() => {
    const update = () => {
      if (scrollAreaRef.current) {
        // Use clientWidth of the scroll area to account for paddings
        setContainerWidth(scrollAreaRef.current.clientWidth)
      }
    }

    // Initial measure
    update()

    // Observe size changes
    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => update())
      if (scrollAreaRef.current) ro.observe(scrollAreaRef.current)
    } else {
      // Fallback to window resize
      window.addEventListener('resize', update)
    }

    return () => {
      if (ro && scrollAreaRef.current) ro.disconnect()
      else window.removeEventListener('resize', update)
    }
  }, [])

  // Calculate width requirements for fixed bar width and gaps
  const numberOfBars = chartData?.labels?.length || 0
  const fixedBarWidth = 45 // Fixed width for each bar
  const fixedGap = 25 // Fixed gap between bars
  const totalBarSpace = numberOfBars * fixedBarWidth
  const gapCount = Math.max(numberOfBars - 1, 0)
  const totalGapSpace = gapCount * fixedGap
  const minRequiredWidth = totalBarSpace + totalGapSpace + 120 // Extra padding

  // Enable scroll only when the content exceeds the visible container
  const enableScroll = minRequiredWidth > containerWidth
  // Fill available width when content fits; otherwise expand to required width
  const finalChartWidthPx = Math.max(minRequiredWidth, containerWidth)
  const chartWidth = `${finalChartWidthPx}px`

  // Outer container: never scrolls, never overflows
  const outerContainerStyle = {
    width: '100%',
    height: '350px',
    position: 'relative',
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    padding: '12px',
    border: '1px solid #e0e0e0',
    boxSizing: 'border-box',
    overflow: 'hidden', // absolutely no scroll on parent
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'stretch',
    maxWidth: '100%'
  }

  // Inner scroll area: only this can scroll horizontally
  const scrollAreaStyle = {
    width: '100%',
    height: '100%',
    overflowX: enableScroll ? 'auto' : 'hidden',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    // Prevent scrollbars on parent
    boxSizing: 'border-box',
    maxWidth: '100%'
  }

  // Chart style: can be wider than parent, but never causes parent to scroll
  const chartStyle = {
    width: chartWidth,
    height: '100%',
    // Remove minWidth to prevent parent overflow
    maxWidth: 'none',
    position: 'relative',
    display: 'block'
  }

  // Enhanced options with fixed bar dimensions
  const enhancedOptions = {
    ...options,
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    // With explicit pixel bar thickness, percentages are not required
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10
      }
    },
    scales: {
      ...options.scales,
      x: {
        ...options.scales?.x,
        ticks: {
          ...options.scales?.x?.ticks,
          // Always allow labels to tilt so long outlet names don't overlap.
          // When the chart is scrollable (many outlets) force the 45° tilt;
          // otherwise let Chart.js pick 0–45° based on available space.
          maxRotation: 45,
          minRotation: enableScroll ? 45 : 0,
          autoSkip: enableScroll ? false : true,
          maxTicksLimit: enableScroll ? undefined : 20
        }
      }
    },
    plugins: {
      ...options.plugins,
      legend: {
        ...options.plugins?.legend,
        display: true,
        position: 'bottom',
        labels: {
          padding: 15,
          font: {
            size: 12
          }
        }
      }
    }
  }

  // Enforce fixed pixel bar thickness so a single bar never grows too wide
  const enhancedData = {
    ...chartData,
    datasets: (chartData?.datasets || []).map((ds) => ({
      ...ds,
      barThickness: fixedBarWidth
    }))
  }

  return (
    <div style={outerContainerStyle}>
      <div style={scrollAreaStyle} ref={scrollAreaRef}>
        <div style={chartStyle}>
          <Bar data={enhancedData} options={enhancedOptions} />
        </div>
      </div>
    </div>
  )
}

export const PieChart = ({ chartData, options }) => {
  return (
    <>
      <Pie data={chartData} />
    </>
  )
}

export const DoughnutChart = ({ chartData, options }) => {
  return (
    <>
      <Doughnut data={chartData} options={options} />
    </>
  )
}
